import {report_error} from "@cling/lib.shared.debug/index"
import {call_function, init as init_faas_} from "@cling/lib.shared.faas"
import {log} from "@cling/lib.shared.logging/index"
import {
    AccountUID,
    create_SessionUID,
    is_Locale,
    ManageSessionRequest,
    SignUpRequest,
    SignUpResponse,
} from "@cling/lib.shared.model/model"
import {from_b64url} from "@cling/lib.shared.model/model_utils"
import {login_or_signup_redirected} from "@cling/lib.web.auth/authenticate"
import {enforce_refresh_promise, LAST_LOCATION_KEY} from "@cling/lib.web.auth"
import {safe_session_storage} from "@cling/lib.web.utils/safe_storage"

export async function handle_auth_redirect(): Promise<boolean> {
    const authenticated = await login_or_signup_redirected()
    if (!authenticated) {
        return false
    }
    if (authenticated.is_error) {
        report_error(authenticated.error)
        return false
    }
    log.info("Social signup or login - creating user in Cling if needed", {
        account_uid: authenticated.account_uid,
    })
    await init_faas(authenticated)
    const sign_up_request_str = safe_session_storage.getItem("signup.request")
    const sign_up_request = sign_up_request_str
        ? from_b64url(SignUpRequest, sign_up_request_str)
        : new SignUpRequest({})
    safe_session_storage.removeItem("signup.request")
    const locale = authenticated.locale || navigator.language
    sign_up_request.locale = is_Locale(locale) ? locale : "en"
    sign_up_request.profile_image_url = authenticated.profile_image_url ?? ""
    sign_up_request.given_name = authenticated.given_name ?? ""
    sign_up_request.family_name = authenticated.family_name ?? ""
    await call_sign_up(sign_up_request)
    return true
}

export function redirect_based_on_sign_up_response(res?: SignUpResponse) {
    if (res?.invite_failed) {
        goto("/invite_failed")
        return
    }
    if (res?.invite_invalid) {
        goto("/invite_invalid")
        return
    }
    if (res?.redirect_to_board_uid) {
        goto(`/c/${res.redirect_to_board_uid}`)
    } else if (res?.full_account?.account_attributes?.create_organization_uid) {
        goto("/c/organizations/add")
    } else {
        const url = safe_session_storage.getItem(LAST_LOCATION_KEY) || "/c"
        if (url !== location.pathname + location.search) {
            goto(url)
        }
    }
}

async function call_sign_up(req: SignUpRequest) {
    const res = await call_function(req, SignUpResponse)
    if (res.already_exists) {
        safe_session_storage.setItem("authenticate.method", "login")
    } else {
        // We need to refresh the id-token, because our claims might have changed.
        try {
            await enforce_refresh_promise()
        } catch (error) {
            report_error("Failed to refresh id-token during sign-up -- ignoring", error)
        }
        safe_session_storage.setItem("authenticate.method", "signup")
    }
    redirect_based_on_sign_up_response(res)
}

async function init_faas({
    account_uid,
    access_token,
}: {
    account_uid: AccountUID
    access_token: string
}) {
    await call_function(new ManageSessionRequest({update_jwt: access_token}))
    // Create the account if it does not exist.
    const faas_trace_provider = () =>
        `|web_app:${process.env.VERSION}:${account_uid}:${create_SessionUID()}|`
    init_faas_({
        request_modifier: (req) => {
            req.headers["x-cling-faas-trace"] = faas_trace_provider()
        },
        on_401: (error) => {
            report_error("Got a 401 from server while creating the user", error)
            goto("/login_failure")
        },
    })
}
